import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import apiClient from "utils/apiClient";
import { ENDPOINTS } from "utils/endpoints";
import { RED, RED_LIGHT, GREEN, GREEN_LIGHT, BORDER, MUTED } from "assets/theme/brandColors";

/**
 * Where the user stands on the level ladder, and the distinction they are
 * climbing toward.
 *
 * The rule this component exists to hold: **levels have no titles.** A rung is
 * a number and an XP figure, nothing else. The only prose on the ladder is the
 * distinction earned at a particular rung — the existing UserStats.distinction
 * system, shown as the destination rather than replaced by a per-level name.
 * The server decides which rungs carry one; this file must never label a rung
 * on its own initiative.
 *
 * Colours come from brandColors only. Two pairings worth stating, because both
 * are easy to get wrong: GREEN is used as a *background* or a dot and never as
 * text (#43a047 on white is 3.3:1, under AA), and text on GREEN_LIGHT is #222
 * for the same reason.
 */

const cardSx = {
  bgcolor: "#fff",
  borderRadius: "14px",
  border: "1.5px solid",
  borderColor: BORDER,
  overflow: "hidden",
  mb: 2,
};

const cardLabelSx = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "1.2px",
  textTransform: "uppercase",
  color: MUTED,
  fontFamily: "Roboto, sans-serif",
};

const fmt = (n) => (n ?? 0).toLocaleString();

function LevelRow({ rung, isLast }) {
  const { level, floor_xp: floorXp, xp_away: xpAway, state, distinction } = rung;
  const isCurrent = state === "current";
  const isReached = state === "reached";

  return (
    <Box sx={{ display: "flex", gap: 1.5, px: 3, alignItems: "stretch" }}>
      {/* Rail: marker + connector. Purely decorative — reached / current /
          locked is also stated in the text beside it, in the level's colour and
          in "N XP away", so nothing here is the sole carrier of a fact.
          The locked marker is a filled BORDER dot rather than a hollow ring:
          a BORDER outline on white is 1.2:1 and reads as a rendering glitch,
          whereas filled it matches the connector line it sits on. */}
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: 14 }}>
        <Box
          sx={{
            mt: "14px",
            width: isCurrent ? 12 : 9,
            height: isCurrent ? 12 : 9,
            flexShrink: 0,
            borderRadius: "50%",
            bgcolor: isCurrent ? RED : isReached ? GREEN : BORDER,
          }}
        />
        {!isLast && <Box sx={{ flex: 1, width: "2px", bgcolor: BORDER, mt: "3px" }} />}
      </Box>

      <Box
        sx={{
          flex: 1,
          py: "11px",
          borderBottom: isLast ? "none" : "1px solid #f5f5f5",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              fontSize: 14,
              fontWeight: isCurrent ? 800 : 600,
              color: isCurrent ? RED : isReached ? "#222" : MUTED,
              fontFamily: "Roboto, sans-serif",
            }}
          >
            Level {level}
            {isCurrent && (
              <Box component="span" sx={{ fontWeight: 700, fontSize: 12, ml: 1 }}>
                — you are here
              </Box>
            )}
          </Box>

          {distinction && (
            <Box
              sx={{
                display: "inline-block",
                mt: "5px",
                fontSize: 11,
                fontWeight: 700,
                fontFamily: "Roboto, sans-serif",
                borderRadius: "999px",
                px: "9px",
                py: "3px",
                ...(isReached || isCurrent
                  ? { color: "#222", bgcolor: GREEN_LIGHT }
                  : { color: RED, bgcolor: RED_LIGHT }),
              }}
            >
              {isReached || isCurrent ? "★ " : "→ "}
              {distinction}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            fontSize: 11.5,
            color: MUTED,
            textAlign: "right",
            flexShrink: 0,
            fontFamily: "Roboto, sans-serif",
          }}
        >
          {xpAway > 0 ? `${fmt(xpAway)} XP away` : `${fmt(floorXp)} XP`}
        </Box>
      </Box>
    </Box>
  );
}

LevelRow.propTypes = {
  rung: PropTypes.shape({
    level: PropTypes.number,
    floor_xp: PropTypes.number,
    xp_away: PropTypes.number,
    state: PropTypes.string,
    distinction: PropTypes.string,
  }).isRequired,
  isLast: PropTypes.bool,
};

function LevelLadder() {
  const [ladder, setLadder] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    apiClient
      .get(ENDPOINTS.users.levelLadder)
      .then((res) => setLadder(res.data))
      .catch(() => setFailed(true));
  }, []);

  // Fail quiet, same as the skill grid below it: a ladder that couldn't load is
  // not worth an error banner on a page whose other half still works.
  if (failed || !ladder) return null;

  const levels = ladder.levels || [];
  const next = ladder.next_distinction;
  const inLevel = Math.max(0, (ladder.points || 0) - (ladder.current_level_floor_xp || 0));
  const levelSpan = Math.max(
    1,
    (ladder.next_level_total_xp || 0) - (ladder.current_level_floor_xp || 0)
  );
  const percent = Math.min(100, Math.max(0, ladder.progress_percent || 0));

  return (
    <Box sx={cardSx}>
      <Box sx={{ px: 3, py: 2, borderBottom: "1px solid", borderColor: BORDER }}>
        <Box sx={cardLabelSx}>Your Progress</Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            gap: 1.5,
            mt: "6px",
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ fontSize: 24, fontWeight: 800, color: RED, fontFamily: "Roboto, sans-serif" }}>
            Level {ladder.level}
          </Box>
          <Box
            sx={{
              fontSize: 12,
              fontWeight: 700,
              color: "#222",
              bgcolor: GREEN_LIGHT,
              borderRadius: "999px",
              px: "10px",
              py: "3px",
              fontFamily: "Roboto, sans-serif",
            }}
          >
            ★ {ladder.distinction}
          </Box>
        </Box>

        <Box
          sx={{ mt: 1.5, height: 8, bgcolor: "#f0f0f0", borderRadius: "4px", overflow: "hidden" }}
        >
          <Box sx={{ width: `${percent}%`, height: "100%", bgcolor: RED, borderRadius: "4px" }} />
        </Box>
        <Box sx={{ fontSize: 12, color: MUTED, mt: "6px", fontFamily: "Roboto, sans-serif" }}>
          {fmt(inLevel)} / {fmt(levelSpan)} XP to Level {ladder.level + 1}
        </Box>

        {/* The destination, stated in one line. This is the sentence the old
            "Level 1 · 450/500 XP" was missing: a number is only progress if
            you can say what it is progress toward. */}
        <Box
          sx={{
            mt: 1.5,
            px: "12px",
            py: "10px",
            bgcolor: RED_LIGHT,
            borderRadius: "10px",
            fontSize: 13,
            lineHeight: 1.5,
            color: RED,
            fontWeight: 600,
            fontFamily: "Roboto, sans-serif",
          }}
        >
          {next
            ? `Next up: ${next.name} at Level ${next.level} — ${next.levels_away} ${
                next.levels_away === 1 ? "level" : "levels"
              } and ${fmt(next.xp_away)} XP away.`
            : `${ladder.distinction} is the last distinction — you've climbed the whole ladder.`}
        </Box>
      </Box>

      <Box sx={{ py: 0.5 }}>
        {levels.map((rung, i) => (
          <LevelRow key={rung.level} rung={rung} isLast={i === levels.length - 1} />
        ))}
      </Box>

      {/* Distinctions carry the whole ladder's shape; the rung list above only
          shows the stretch the user is currently on. */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderTop: "1px solid",
          borderColor: BORDER,
          bgcolor: "#fbfbfb",
        }}
      >
        <Box sx={cardLabelSx}>Distinctions</Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", mt: "8px" }}>
          {(ladder.distinctions || []).map((d) => (
            <Box
              key={d.name}
              sx={{
                fontSize: 11,
                fontWeight: 700,
                fontFamily: "Roboto, sans-serif",
                borderRadius: "999px",
                px: "10px",
                py: "4px",
                border: "1.5px solid",
                ...(d.state === "current"
                  ? { color: RED, bgcolor: RED_LIGHT, borderColor: RED }
                  : d.state === "earned"
                  ? { color: "#222", bgcolor: GREEN_LIGHT, borderColor: GREEN_LIGHT }
                  : { color: MUTED, bgcolor: "#fff", borderColor: BORDER }),
              }}
            >
              {d.name}
              <Box component="span" sx={{ fontWeight: 400, ml: "5px" }}>
                Lv {d.from_level}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

export default LevelLadder;
